import React, { useState } from 'react';
import { sanitizeInput, validateEmail } from '../../utils/inputValidation';
import './AuthForms.css';

/**
 * ForgotPassword Component
 *
 * Allows users to request a password reset email
 * - Email input with validation
 * - Success message after submission
 * - Back to login link
 */
const ForgotPassword = ({ onResetPassword, onBackToLogin, isLoading = false }) => {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const value = e.target.value;
    const sanitized = sanitizeInput(value, { lowerCase: true });
    setEmail(sanitized);
    setValidationError(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailResult = validateEmail(email);
    if (!emailResult.valid) {
      setValidationError(emailResult.error);
      return;
    }

    try {
      const result = await onResetPassword(emailResult.sanitized);

      if (result?.error) {
        setError(result.error);
      } else {
        setIsSubmitted(true);
      }
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    }
  };

  // Success view after submission
  if (isSubmitted) {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <div className="auth-form-header">
            <h2>Check Your Email</h2>
            <p className="auth-form-subtitle">
              We've sent a password reset link to your email address.
            </p>
          </div>

          <div className="auth-success-banner">
            <p>
              <strong>Email sent!</strong> Please check your inbox (and spam folder) for
              an email from ThryveLoop with instructions to reset your password.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              The link will expire in 1 hour for security reasons.
            </p>
          </div>

          <button className="btn-modern secondary" onClick={onBackToLogin} style={{ width: '100%' }}>
            Back to Login
          </button>

          <div className="privacy-notice" style={{ marginTop: '1.5rem' }}>
            <p>
              <strong>Didn't receive the email?</strong> Check your spam folder, or{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => setIsSubmitted(false)}
              >
                try again
              </button>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Request form
  return (
    <div className="auth-form-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-form-header">
          <h2>Reset Your Password</h2>
          <p className="auth-form-subtitle">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        {error && (
          <div className="auth-error-banner" role="alert">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleChange}
            placeholder="you@example.com"
            autoComplete="email"
            required
            disabled={isLoading}
            className={validationError ? 'input-error' : ''}
            autoFocus
          />
          {validationError && (
            <span className="error-message">{validationError}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn-modern primary auth-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </button>

        <div className="auth-form-footer">
          <p>
            Remember your password?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onBackToLogin}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </p>
        </div>

        <div className="privacy-notice">
          <p>
            <strong>Privacy Note:</strong> We'll only send password reset emails to
            registered accounts. If you don't receive an email, the address may not be
            registered.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ForgotPassword;
